import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  InputList,
  SelectInput,
  Size,
  validationEngineFromInputInformationList,
  ValidationStyle,
} from '@utahdts/dts-react-common';
import useRefLazy from '../misc/hooks/useRefLazy';
import companyInputs from './inputs/companyInputs';
import webservice from '../app/webservice/webservice';
import appStore from '../app/mobx/appStore';
import { usePrevious } from '../misc/hooks/usePrevious';
import roles from '../app/roles/roles';
import security from '../app/roles/security';
import menuStore from '../app/mobx/menuStore';
import Pages from '../app/pages/Pages';
import { statuses } from '../misc/Shared';

const SingleCompany = () => {
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState(undefined);
  const [users, setUsers] = useState([]);
  const prevOrg = usePrevious(appStore.currentOrg);

  // Orgs where the user holds the Org Administrator role, identified by their
  // BUSINESS KEY (fein). Every company/user read on this page selects by fein
  // — never a document id (see .claude/rules/13-no-native-ids-as-keys.md).
  // With exactly one, the page pre-loads that company; with several, a picker
  // is shown first. When the role entries resolve nothing (e.g. org chosen via
  // Switch Company), fall back to the already-selected org (fein). (Optional
  // call: some test suites automock the security module.)
  const orgAdminFeins = security.orgAdminFeins?.() || [];
  const [selectedFein, setSelectedFein] = useState(() => {
    if (orgAdminFeins.length === 1) {
      return orgAdminFeins[0];
    }
    const currentFein = appStore.currentOrg;
    return currentFein &&
      (orgAdminFeins.length === 0 || orgAdminFeins.includes(currentFein))
      ? currentFein
      : null;
  });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [primaryContact, setPrimary] = useState({
    old: null,
    new: null,
    value: null,
  });
  const [alternativeContact, setAlternative] = useState({
    old: null,
    new: null,
    value: null,
  });
  const [inputValues, setInputValues] = useState(() => ({
    id: null,
    name: null,
    fein: null,
    address: null,
    address2: null,
    city: null,
    state: null,
    zip: null,
  }));
  const inputValuesRef = useRef();
  inputValuesRef.current = inputValues;

  // The FEIN as last loaded from the server, so submit() can detect a change.
  const loadedFeinRef = useRef(null);

  const [validationEngineStyle, setValidationEngineStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const validationEngineRef = useRef(undefined);

  const inputs = useRefLazy(() =>
    companyInputs(
      {
        inputsOnChange: (field, value) =>
          setInputValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      true,
      users,
      {
        feinDisabled: !security.hasUserRole?.([roles.admin, roles.dev]),
      },
    ),
  );

  const [validationEngine, setValidationEngine] = useState(undefined);
  validationEngineRef.current = validationEngine;
  if (validationEngine) {
    validationEngine.validationStyle = validationEngineStyle;
  }

  const submit = () => {
    setValidationEngineStyle(ValidationStyle.ALWAYS);
    if (
      validationEngineRef.current &&
      validationEngineRef.current.isValid(inputValuesRef.current) &&
      primaryContact.new
    ) {
      setBusy(true);
      setError(undefined);
      onSave(
        {
          ...inputValues,
          primaryContact: primaryContact.new,
          alternativeContact: alternativeContact.new,
        },
        primaryContact.old,
        alternativeContact.old,
      ).catch(() => setBusy(false));
    } else if (!primaryContact.new) {
      setError('A primary contact is required.');
    }
  };

  const onSave = (company, oldPrimary, oldAlt) => {
    // Changing the FEIN re-opens the org for review (server flips it back to
    // Pending); detect that here so we can drop the now-inaccessible org.
    const feinChanged =
      loadedFeinRef.current != null && company.fein !== loadedFeinRef.current;
    let queries = [];
    company.primaryContact &&
      company.primaryContact.id &&
      queries.push(checkContact(company, oldPrimary, 'primaryContact'));
    company.alternativeContact &&
      company.alternativeContact.id &&
      queries.push(checkContact(company, oldAlt, 'alternativeContact'));
    queries.push(webservice.company.save(company));
    return Promise.all(queries).then(() => {
      setBusy(false);
      if (feinChanged) {
        handleFeinChangeReview(company.id);
      }
    });
  };

  // After an FEIN change the org is back in Pending review. If the signed-in
  // user is a primary/alternate contact of that org they lose access to it.
  // When it was the only org they org-administer, the whole app is locked down
  // to the review notice + Sign Out; otherwise the pending org is just dropped
  // from the current selection so they can switch to another company.
  const handleFeinChangeReview = (orgId) => {
    const user = appStore.loggedInUser;
    if (!user || !orgId) {
      return;
    }
    const isContact = (user.roles || []).some(
      (role) =>
        String(role.org) === String(orgId) &&
        (role.primaryContact || role.alternativeContact),
    );
    if (!isContact) {
      return;
    }

    const orgAdminIdsAll = security.orgAdminOrgIds?.() || [];
    const otherOrgAdminIds = orgAdminIdsAll.filter(
      (id) => String(id) !== String(orgId),
    );

    if (String(appStore.currentOrgRecord?.id) === String(orgId)) {
      appStore.currentOrg = undefined;
    }

    if (otherOrgAdminIds.length === 0) {
      // Their only org is now in review: lock the app to the review notice.
      appStore.userOrgs = [];
      appStore.underReview = true;
      appStore.showMessage = true;
    }

    menuStore.setupMenu?.();
    Pages.home.forward();
  };

  const checkContact = (company, oldContact, field) => {
    if (
      (company[field] ? company[field].id : undefined) !==
      (oldContact ? oldContact.id : undefined)
    ) {
      let fieldRole =
        oldContact && oldContact.roles
          ? oldContact.roles.findIndex(
              (role) => role.org === company.id && role[field],
            )
          : -1;
      if (fieldRole >= 0) {
        let userRole = oldContact.roles[fieldRole];
        userRole[field] = false;
        saveContact(oldContact);
      }
      return setContact(company[field], field);
    } else {
      return setContact(oldContact, field);
    }
  };

  const setContact = (user, field) => {
    let isOrgAdmin = user.roles.findIndex(
      (role) => role.org === inputValues.id && role.role === roles.orgAdmin,
    );
    let isField = user.roles.findIndex(
      (role) => role.org === inputValues.id && role[field],
    );
    if (isOrgAdmin >= 0) {
      let userRoles = user.roles[isOrgAdmin];
      userRoles[field] = true;
      isField >= 0 && isOrgAdmin !== isField && user.roles.splice(isField, 1);
    } else if (isField >= 0) {
      user.roles[isField].role = roles.orgAdmin;
    } else {
      user.roles.push({
        org: inputValues.id,
        role: roles.orgAdmin,
        [field]: true,
      });
    }
    return saveContact(user);
  };

  const saveContact = (user) => webservice.user.save(user);

  const formatUsers = (data) => {
    const options = data.usersByOrg.map((user) => ({
      ...user,
      value: user.id,
      label: user.name,
    }));
    const primary = data.usersByOrg.find((user) =>
      user.roles.find((role) => role.primaryContact),
    );
    const alternative = data.usersByOrg.find((user) =>
      user.roles.find((role) => role.alternativeContact),
    );
    setPrimary({
      old: primary,
      value: primary ? primary.id : undefined,
      new: primary,
    });
    setAlternative({
      old: alternative,
      value: alternative ? alternative.id : undefined,
      new: alternative,
    });
    setUsers(options);
    setBusy(false);
  };

  const getInfo = (orgId) => {
    orgId &&
      webservice.company
        .get(orgId)
        .then((org) => {
          loadedFeinRef.current = org?.fein ?? null;
          setInputValues({ ...org });
        })
        .catch((e) => {
          console.error('[SingleCompany] getInfo failed', e);
          setBusy(false);
        });
  };

  const getUsers = (orgId) => {
    orgId &&
      webservice.user
        .perOrg(orgId)
        .then(formatUsers)
        .catch((e) => {
          console.error('[SingleCompany] getUsers failed', e);
          // If users cannot be loaded, clear any busy state so UI does not lock
          setBusy(false);
        });
  };

  const pickCompany = (org) => {
    appStore.selectOrgRecord({
      ...org,
      label: org.name || org.fein,
      value: org.id,
    });
    setSelectedFein(org.fein);
  };

  useEffect(() => {
    if (!validationEngineRef.current) {
      setValidationEngine(
        validationEngineFromInputInformationList(inputs.current),
      );
    }
    // Administrators do not get the Company Info page (they manage companies
    // through the admin listings); same predicate as the route/menu guard.
    // (Strict false so automocked security in tests does not redirect.)
    if (appStore.loggedInUser && security.canManageCompanyInfo?.() === false) {
      Pages.home.forward();
    }
    // If the app is locked into review, there is nothing to manage here.
    if (appStore.underReview) {
      Pages.home.forward();
    }
  }, []);

  // Follow Switch Company: when the selected org changes elsewhere and the
  // user org-administers it, load that company here. Matched by fein.
  useEffect(() => {
    const currentFein = appStore.currentOrg;
    if (
      prevOrg !== appStore.currentOrg &&
      currentFein &&
      (orgAdminFeins.length === 0 || orgAdminFeins.includes(currentFein))
    ) {
      setSelectedFein(currentFein);
    }
  }, [appStore.currentOrg]);

  // Load the selected company; with several org-admin orgs and none selected
  // yet, load the picker options instead (inactivated companies excluded).
  useEffect(() => {
    if (selectedFein) {
      getUsers(selectedFein);
      getInfo(selectedFein);
    } else if (orgAdminFeins.length > 1) {
      setBusy(true);
      Promise.all(
        orgAdminFeins.map((fein) =>
          webservice.company.get(fein).catch(() => null),
        ),
      ).then((orgs) => {
        const approved = orgs.filter(
          (org) => org && org.status === statuses.APPROVED,
        );
        if (approved.length === 1) {
          pickCompany(approved[0]);
        } else {
          setCompanyOptions(approved);
        }
        setBusy(false);
      });
    }
  }, [selectedFein]);

  // Company picker: shown when the user org-administers several companies and
  // none is selected yet. Clicking an entry opens the editable form below.
  if (!selectedFein && orgAdminFeins.length > 1) {
    return (
      <div key="company-picker" className="home-wrapper">
        <div className="home-content">
          <h1 className="mb-zero">Company Info</h1>
        </div>
        <div>
          <p>Select the company you want to manage:</p>
          <ul className="actions">
            {companyOptions.map((org) => (
              <li
                key={org.id}
                className="actions-item"
                onClick={() => pickCompany(org)}
              >
                {org.name || org.fein}
              </li>
            ))}
          </ul>
          {!isBusy && companyOptions.length === 0 && (
            <p>No active companies are available for your account.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div key="listings" className="home-wrapper">
      <div className="home-content">
        <h1 className="mb-zero">Company Info</h1>
      </div>
      <div>
        <InputList
          inputs={Object.values(inputs.current)}
          data={inputValues}
          validationEngine={validationEngine}
          className="stack-form flex flex-wrap half-form"
        />
        <section className="stack-form">
          <SelectInput
            field="contactPrimary"
            label="Primary Contact:"
            multiSelect={false}
            value={primaryContact.value}
            onChange={(field, value) =>
              setPrimary((oldValues) => ({
                ...oldValues,
                value: value,
                new: users.find((user) => user.id === value),
              }))
            }
            options={users}
            allowClear={false}
            size={Size.FULL_WIDTH}
            className="required"
          />
          <SelectInput
            field="alternativeContact"
            label="Alternative Contact:"
            multiSelect={false}
            value={alternativeContact.value}
            onChange={(field, value) =>
              setAlternative((oldValues) => ({
                ...oldValues,
                value: value,
                new: users.find((user) => user.id === value),
              }))
            }
            options={users}
            allowClear={false}
            size={Size.FULL_WIDTH}
          />
        </section>
        {error && <div className="form-error">{error}</div>}
        <Button label="Save" busy={isBusy} onClick={submit} key="save-button" />
      </div>
    </div>
  );
};

export default observer(SingleCompany);
